import React, { useEffect, useState } from "react";
import { useParams, Link, Navigate } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import { getArticle, getArticles } from "../lib/api";

export default function ArticlePage() {
  const { slug } = useParams();
  const [article, setArticle] = useState(null);
  const [more, setMore] = useState([]);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    setArticle(null);
    getArticle(slug)
      .then(d => { setArticle(d); window.scrollTo({ top: 0, behavior: "instant" }); })
      .catch(() => setNotFound(true));
    getArticles().then(all => setMore(all.filter(a => a.slug !== slug).slice(0, 3)));
  }, [slug]);

  if (notFound) return <Navigate to="/journal" replace />;
  if (!article) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="font-serif-ml italic text-[var(--ml-text-dim)] text-2xl">Setting the type…</div>
      </div>
    );
  }

  return (
    <article className="grain" data-testid={`article-${article.slug}`}>
      {/* Hero */}
      <section className="relative min-h-[80vh] w-full overflow-hidden">
        <div className="absolute inset-0 kenburns">
          <img src={article.hero_image} alt={article.title} className="w-full h-full object-cover" />
        </div>
        <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/45 to-[var(--ml-bg)]" />
        <div className="absolute inset-0 vignette" />

        <div className="relative z-10 container-ml min-h-[80vh] flex flex-col">
          <div className="pt-32 md:pt-36">
            <Link to="/journal" data-testid="back-to-journal" className="inline-flex items-center gap-2 text-[0.65rem] tracking-[0.28em] uppercase text-[var(--ml-text-dim)] hover:text-[var(--ml-gold)] transition-colors">
              <ArrowLeft size={14} /> Stories
            </Link>
          </div>
          <div className="flex-1 flex items-end pb-20 md:pb-28">
            <div className="max-w-5xl reveal-up">
              <p className="font-mono-ml text-[0.65rem] tracking-[0.3em] uppercase text-[var(--ml-gold)] mb-6">
                {article.category}
              </p>
              <h1 className="font-serif-ml text-5xl sm:text-6xl md:text-[9rem] leading-[0.85] tracking-[-0.025em] text-[var(--ml-text)] mb-8">
                {article.title}
              </h1>
              <p className="font-serif-ml italic text-2xl md:text-3xl text-[var(--ml-text-dim)] max-w-3xl leading-snug">
                {article.subtitle}
              </p>
              <p className="font-mono-ml text-[0.65rem] tracking-[0.25em] uppercase text-[var(--ml-text-muted)] mt-8">
                By {article.author} · {article.read_time} min · {new Date(article.published_at).toLocaleDateString("en-GB", { day: "numeric", month: "long", year: "numeric" })}
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Body */}
      <section className="container-ml py-24 md:py-32">
        <div className="max-w-3xl mx-auto">
          <p className="font-serif-ml italic text-2xl md:text-3xl text-[var(--ml-gold)] mb-12 leading-snug">
            {article.excerpt}
          </p>
          {article.body.map((p, i) => (
            <p key={i} className={`text-[var(--ml-text)] text-lg md:text-xl leading-[1.85] mb-8 ${i === 0 ? "drop-cap" : ""}`}>
              {p}
            </p>
          ))}
          {article.source_url && (
            <div className="mt-12 border-t border-white/10 pt-10 text-center" data-testid="article-source">
              <p className="overline mb-4">Source</p>
              <a
                href={article.source_url}
                target="_blank"
                rel="noopener noreferrer"
                className="btn-ml-gold inline-block"
              >
                Read the original on {article.source_name || "the original publisher"}
              </a>
            </div>
          )}
          <div className="rule my-20" />
          <p className="text-center font-serif-ml italic text-[var(--ml-text-dim)] text-xl">
            — by {article.author}
          </p>
        </div>
      </section>

      {/* More to read */}
      {more.length > 0 && (
        <section className="container-ml pb-32" data-testid="article-more">
          <p className="overline mb-8 text-center">Continue reading</p>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-10">
            {more.map(a => (
              <Link key={a.slug} to={`/journal/${a.slug}`} data-testid={`more-article-${a.slug}`} className="group block">
                <div className="aspect-[4/3] overflow-hidden border border-white/5 mb-5">
                  <img src={a.hero_image} alt={a.title} loading="lazy" className="w-full h-full object-cover moody-img" />
                </div>
                <p className="font-mono-ml text-[0.6rem] tracking-[0.3em] uppercase text-[var(--ml-gold)] mb-3">{a.category}</p>
                <h3 className="font-serif-ml text-2xl md:text-3xl tracking-tight leading-[1.05] text-[var(--ml-text)] group-hover:text-[var(--ml-gold)] transition-colors">
                  {a.title}
                </h3>
              </Link>
            ))}
          </div>
        </section>
      )}
    </article>
  );
}
